import type { ShopConfiguration } from "@/shared/types/shop";
import type { Facet, LayerConfig, LayoutSection, SortValue } from "../types";

export interface LayerRemoteData {
  recommendationRules: unknown;
  builderConfig: unknown;
  baseUrl: string;
}

// Forme lette della configurazione: il backend e' un proxy, si legge come faceva il legacy (se manca una sezione
// obbligatoria si lancia, come oggi).
type Layout = LayoutSection &
  Required<Pick<LayoutSection, "card" | "desktop" | "mobile" | "sort" | "language" | "icebreaker">>;
interface SetupConfig {
  productUrl: Record<string, unknown>;
  groupPrice: { priceGroupId: unknown; currencyCode: unknown };
  dynamicFacets: { isActive?: boolean };
  webhook: { isActive?: boolean; key?: string; url?: string };
  seoSettings?: { isActive?: boolean; searchWord?: string; csvData?: Record<string, unknown>[] } | false;
  ga4: unknown;
  layerVersion: unknown;
}
interface Setup {
  results: Record<string, { value: unknown; label: string }>;
  productImage?: { main?: Record<string, unknown> };
  config: SetupConfig;
}

/** Voce di card per il n-esimo valore dei risultati: attiva se presente, con la label come chiave. */
function cardItem(values: { value: unknown; label: string }[], n: number) {
  const found = values.filter((item) => parseInt(String(item.value)) == n);
  const isActive = found.length > 0;
  return { isActive, key: isActive ? found[0]!.label : "" };
}

function toFilters(layout: Layout, setup: Setup): Record<string, unknown> {
  const filters: Record<string, Record<string, unknown>> = {};
  (layout.facets as Facet[]).forEach((value, index) => {
    filters[value.name] = {
      isActive: value.active,
      label: value.label,
      type: value.isCategory ? (value.type!.includes("MC") ? "checkbox-MC" : "checkbox-SC") : value.type,
      filterSorting: value.filterSorting,
      isCategory: value.isCategory,
      order: index,
      withSearch: value.withSearch,
      level: value.isCategory ? (value.type!.includes("tree") ? 0 : -1) : [],
    };
  });
  filters.listingPrice = { isActive: false };
  if (setup.config.dynamicFacets.isActive) {
    filters.dynamicFacets = {
      type: layout.dynamicFacets!.dynamicType,
      filterSorting: layout.dynamicFacets!.dynamicSorting,
      withSearch: layout.dynamicFacets!.dynamicSearch,
    };
  }
  return filters;
}

function toSeoSettings(setup: Setup): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const seo = setup.config.seoSettings;
  if (seo) {
    if (seo.isActive) {
      result.isActive = true;
      result.searchWord = seo.searchWord;
    }
    result.attributes = Object.keys(seo.csvData ?? []).map((key) => {
      const row = (seo.csvData as unknown as Record<string, Record<string, unknown>>)[key]!;
      return Object.keys(row).map((attr, index) => ({ key: attr, value: Object.values(row)[index] }));
    });
  }
  return result;
}

function toSorts(values: SortValue[]): Record<string, unknown>[] {
  return values
    .filter((item) => item.active)
    .map((item) => {
      const sort: Record<string, unknown> = { label: item.label, value: item.value, key: item.key };
      if (item.selectedList && item.selectedList.length > 0) {
        sort.tiebrakers = item.selectedList.map((sub) => ({ key: sub.label, value: sub.sort }));
      }
      return sort;
    });
}

/**
 * Configurazione dello shop -> config del search layer (anteprima dello shell). Funzione pura: i dati remoti
 * (regole di raccomandazione, builder) arrivano da fuori. L'ordine delle chiavi e i refusi sono il contratto
 * verso il layer, verificato al byte contro l'output della vecchia `Utils.fromConfigToConfig`.
 */
export function toLayerConfig(configuration: ShopConfiguration, remote: LayerRemoteData): LayerConfig {
  const layout = configuration.layout as unknown as Layout;
  const setup = configuration.setup as unknown as Setup;
  const { desktop, mobile } = layout;
  const builderEnabled = layout.builder?.isActive ?? false;
  const configurableAttributes = layout.card.configurableAttributes!.filter((item) => item.key);
  const results = Object.values(setup.results);

  return {
    baseUrl: remote.baseUrl,
    shopId: configuration.key,
    accentColor: layout.primaryColor,
    display: desktop.layout,
    show: {
      type: desktop.typing,
      id: { desktop: "search", mobile: "search" },
      name: { desktop: "search", mobile: "search2" },
      disableScroll: true,
    },
    queryOptions: {
      rows: { desktop: desktop.perPage, mobile: mobile.perPage },
      show_variants: true,
      suggestions: 2,
      spellcheck: 2,
    },
    recommendationRules: remote.recommendationRules,
    hover: {
      secondImage: false,
      changeImage: configurableAttributes.length > 0,
      isActive: configurableAttributes.length > 0,
      attributes: configurableAttributes,
    },
    priceGroup: {
      name: "default",
      id: setup.config.groupPrice.priceGroupId,
      currency: setup.config.groupPrice.currencyCode,
      quantity: "0.0000",
      currencySymbol: "€",
      format: { decimals: 2, dec_divider: layout.decimalSeparator || ".", migl__divider: "" },
    },
    card: {
      title: cardItem(results, 1),
      desc: cardItem(results, 2),
      url: { isActive: true, key: Object.keys(setup.config.productUrl)[0]! },
      img: { isActive: true, key: Object.keys(setup.productImage?.main ?? { image: "image" })[0]! },
      isinstock: cardItem(results, 3),
      sku: cardItem(results, 4),
      type: cardItem(results, 5),
      brand: cardItem(results, 6),
      rating: cardItem(results, 7),
      custom: cardItem(results, 8),
      listingPrice: { isActive: true, key: "listingPrice" },
      sellingPrice: { isActive: true, key: "sellingPrice" },
    },
    placeholders: {
      searchbar: "Search for a product",
      resultsFor: "Results for",
      filterBy: "Filter By",
      products: "Products",
      labelTiles: "Search or filter by Category",
      labelTrendingCategories: "Trending Categories",
      zeroPrice: layout.isZeroOrNull ? layout.zeroOrNull : "",
      outOfStock: "Out of stock",
      inStock: "In stock",
      showFilters: "Show Filters",
      hideFilters: "Hide Filters",
      clearFilters: "Clear Filters",
      activeFilters: "Active Filters",
      suggestedWords: "Suggested Words",
      wholeCategory: "Tutta la categoria",
      mainCategories: "Categorie principali",
      noCategory: "There are no results that match your search",
      fromSlider: "From",
      lastSearches: "Ultime ricerche",
      toSlider: "To",
      filterSearchbar: "Cerca",
      noProducts: "Nessun prodotto per ",
      noMatch: "Nessuna corrispondenza",
      popularSearch: "Prova con una delle parole più utilizzate:",
      reset: "Reset",
      show: "Mostra",
      showAll: "Show all",
    },
    voiceSearch: {
      active: layout.language.isActive,
      desktop: layout.language.isActive,
      mobile: layout.language.isActive,
      googleAPIKEY: "",
      language: layout.language.language,
    },
    showcaseItems: { desktop: desktop.showcaseItems, mobile: mobile.showcaseItems },
    triggers: {
      sort: layout.sort.active,
      dynamicBanner: layout.dynamicBanner,
      wordSuggention: true,
      icebreaker: { isActive: layout.icebreaker.isActive, number: layout.icebreaker.number },
      dynamicFacetsLabels: setup.config.dynamicFacets.isActive,
      infiniteScrolling: { desktop: desktop.paging, mobile: mobile.paging },
      wordsHistory: {
        desktop: { isActive: desktop.showcaseLastSearches, timeframe: desktop.showcaseLastSearchesOption },
        mobile: { isActive: mobile.showcaseLastSearches, timeframe: mobile.showcaseLastSearchesOption },
      },
      barcode: mobile.barcode,
      placeholder: layout.placeholder,
      logo: desktop.logoUrl,
      raccomandationsCategories: {
        desktop: desktop.showcaseRecommendationsCategories ? desktop.recommendationCategoriesItems : [],
        mobile: mobile.showcaseRecommendationsCategories ? mobile.recommendationCategoriesItems : [],
      },
      raccomandationsProducts: [],
      categoryTiles: {
        desktop: desktop.showcaseMainCategories ? desktop.showcaseMainCategoriesItems : [],
        mobile: mobile.showcaseMainCategories ? mobile.showcaseMainCategoriesItems : [],
      },
    },
    webhook: {
      key: setup.config.webhook.isActive && setup.config.webhook.key ? setup.config.webhook.key : "",
      fields: {
        sellingPrice:
          setup.config.webhook.isActive && setup.config.webhook.url ? setup.config.webhook.url : "",
      },
    },
    autocomplete: {
      desktop: false,
      mobile: false,
      searchAttribute: ["title", "url", "image"],
      matchCategories: true,
      rows: 10,
      minChars: { desktop: desktop.minChars, mobile: mobile.minChars },
      highlightMode: "normal",
      limit: 6,
      item: { price: false, image: false },
    },
    sorts: toSorts(layout.sort.values!),
    filters: toFilters(layout, setup),
    seoSettings: toSeoSettings(setup),
    ga4: setup.config.ga4,
    version: setup.config.layerVersion,
    promotions: {
      autocomplete: {
        desktop: { isActive: false, img: "", url: "" },
        mobile: { isActive: false, img: "", url: "" },
      },
      firstScreen: {
        desktop: {
          isActive: desktop.showcaseBanner,
          img: desktop.showcaseBannerSrc,
          url: desktop.showcaseBannerUrl,
          name: desktop.showcaseBannerName,
        },
        mobile: {
          isActive: mobile.showcaseBanner,
          img: mobile.showcaseBannerSrc,
          url: mobile.showcaseBannerUrl,
          name: mobile.showcaseBannerName,
        },
      },
    },
    stickers: [],
    alert: {},
    noProducts: { mostSearched: [], img: "" },
    builder: builderEnabled,
    builderConfig: remote.builderConfig,
  };
}
